import React, { useEffect } from "react";
import { removeGlobalContextProperty, setGlobalContextProperty } from "@jsq/observability";

/**
 * Puts `arena_id` and `surface` on the host's RUM global context, so every Fay
 * action and error carries them without each call site repeating the pair.
 *
 * `surface` has to be told to us: the package never reads a host app's context,
 * both hosts serve Fay from the same route base, and neither the bundle nor the
 * URL distinguishes GPX from Canopy. Each host wraps its Fay surface once and
 * declares which one it is.
 *
 * Both keys are global to the host's RUM application, while Fay is one subtree
 * of it, so they are removed on unmount — otherwise every later action in the
 * host would keep reporting the arena the user last opened in Fay.
 */
export type FaySurface = "gpx" | "canopy";

export type FayObservabilityProviderProps = {
  /** Which host renders this Fay surface. */
  surface: FaySurface;
  /** Undefined until the host app resolves it; the key appears once it does. */
  arenaId: number | undefined;
  children: React.ReactNode;
};

export const FayObservabilityProvider: React.FC<FayObservabilityProviderProps> = ({
  surface,
  arenaId,
  children,
}) => {
  useEffect(() => {
    setGlobalContextProperty("surface", surface);
    return () => removeGlobalContextProperty("surface");
  }, [surface]);

  useEffect(() => {
    if (arenaId === undefined) {
      return;
    }
    setGlobalContextProperty("arena_id", arenaId);
    return () => removeGlobalContextProperty("arena_id");
  }, [arenaId]);

  return <>{children}</>;
};
